/**
 * Geocoding Service
 * Provides address-to-coordinates conversion using the geocode Edge Function
 */

import { supabase } from "./supabase";
import type { GeocodeSource } from "../types/organization.types";

export interface GeocodeResult {
  latitude: number;
  longitude: number;
  formatted_address?: string;
  confidence?: number;
  source: GeocodeSource;
}

export interface AddressComponents {
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
}

/**
 * Geocode an address using the Edge Function
 */
export async function geocodeAddress(
  address: AddressComponents | string,
  options?: {
    provider?: "nominatim" | "google" | "census";
    updateRecord?: {
      table: "organizations" | "locations";
      id: string;
    };
  },
): Promise<GeocodeResult | null> {
  try {
    const body: Record<string, unknown> = {
      provider: options?.provider || "nominatim",
    };

    if (typeof address === "string") {
      body.full_address = address;
    } else {
      Object.assign(body, address);
    }

    if (options?.updateRecord) {
      body.update_record = options.updateRecord;
    }

    const { data, error } = await supabase.functions.invoke("geocode", {
      body,
    });

    if (error) {
      console.error("Geocode function error:", error);
      return null;
    }

    if (data?.error) {
      console.error("Geocode returned error:", data.error);
      return null;
    }

    return data as GeocodeResult;
  } catch (error) {
    console.error("Geocoding error:", error);
    return null;
  }
}

/**
 * Build a full address string from components
 */
export function buildAddressString(components: AddressComponents): string {
  const parts = [
    components.address_line1,
    components.address_line2,
    components.city,
    components.state,
    components.postal_code,
    components.country || "USA",
  ].filter(Boolean);

  return parts.join(", ");
}

/**
 * Check if address components have enough data for geocoding
 */
export function hasGeocodableAddress(components: AddressComponents): boolean {
  // Need at least city/state OR postal code for reasonable geocoding
  const hasCity = !!components.city;
  const hasState = !!components.state;
  const hasPostalCode = !!components.postal_code;
  const hasStreet = !!components.address_line1;

  return (
    (hasCity && hasState) ||
    hasPostalCode ||
    (hasStreet && (hasCity || hasPostalCode))
  );
}

/**
 * Calculate distance between two points using Haversine formula (client-side)
 * Returns distance in miles
 */
export function calculateDistanceMiles(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Format coordinates for display
 */
export function formatCoordinates(
  lat: number,
  lon: number,
  precision: number = 6,
): string {
  const latDir = lat >= 0 ? "N" : "S";
  const lonDir = lon >= 0 ? "E" : "W";
  return `${Math.abs(lat).toFixed(precision)}° ${latDir}, ${Math.abs(lon).toFixed(precision)}° ${lonDir}`;
}
